import {
  ImageBlobPaginatedList,
  ImageObjectPaginatedList,
  ImagesImageIdBlobsBlobIdDeleteRequest,
  ImagesImageIdBlobsBlobIdPatchRequest,
  ImagesImageIdBlobsBlobIdReanalyzePostRequest,
  ImagesImageIdBlobsPostRequest,
  ImagesImageIdDeleteRequest,
  ImagesImageIdPatchRequest,
  ImagesPostRequest,
} from '@nativewaves/nw-cloud-sdk/content'
import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query'

import { ContentAPI } from 'services/api'
import { imageObjectQueryKeys } from 'services/queryKeys'
import { updateListCachesByCreation, updateListCachesByDeletion, updateListCachesByUpdate } from 'utils'

export const imageObjectMutationKeys = {
  delete: () => ['content', 'image-object', 'DELETE'] as const,
}

type UseImageObjectCreateMutationProps = {
  originId?: string
}

export const useImageObjectCreateMutation = (props: UseImageObjectCreateMutationProps) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: ImagesPostRequest) => ContentAPI.imageObjects.imagesPost(params),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: imageObjectQueryKeys.lists(),
      })
    },
    onSettled: (data, error) => {
      if (!!data && !error) {
        queryClient.setQueriesData<InfiniteData<ImageObjectPaginatedList>>(
          { queryKey: imageObjectQueryKeys.lists() },
          updateListCachesByCreation(data, { idKey: 'id', originId: props.originId }),
        )
      }
    },
  })
}

export const useImageObjectUpdateMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: ImagesImageIdPatchRequest) => ContentAPI.imageObjects.imagesImageIdPatch(params),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: imageObjectQueryKeys.lists() })
    },
    onSettled: (data, error, variables) => {
      if (!error && !!data) {
        queryClient.resetQueries({ queryKey: imageObjectQueryKeys.lists() })

        queryClient.setQueriesData<InfiniteData<ImageObjectPaginatedList>>(
          { queryKey: imageObjectQueryKeys.lists() },
          updateListCachesByUpdate(data, { idKey: 'id', idToMatch: variables.imageId }),
        )

        return queryClient.invalidateQueries({ queryKey: imageObjectQueryKeys.details() })
      }
    },
  })
}

export const useImageObjectDeleteMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: imageObjectMutationKeys.delete(),
    mutationFn: (params: ImagesImageIdDeleteRequest) => ContentAPI.imageObjects.imagesImageIdDelete(params),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: imageObjectQueryKeys.lists() })
    },
    onSettled: (_, error, variables) => {
      if (!error) {
        queryClient.setQueriesData<InfiniteData<ImageObjectPaginatedList>>(
          { queryKey: imageObjectQueryKeys.lists() },
          updateListCachesByDeletion({ idKey: 'id', idToMatch: variables.imageId }),
        )
      }
    },
  })
}

export const useImageBlobCreateMutation = (props?: { originId?: string }) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: ImagesImageIdBlobsPostRequest) => ContentAPI.imageObjects.imagesImageIdBlobsPost(params),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: imageObjectQueryKeys.blobLists() })
    },
    onSettled: (data, error) => {
      if (!error && !!data) {
        queryClient.resetQueries({ queryKey: imageObjectQueryKeys.blobLists() })

        queryClient.setQueriesData<InfiniteData<ImageBlobPaginatedList>>(
          { queryKey: imageObjectQueryKeys.blobLists() },
          updateListCachesByCreation(data, { idKey: 'id', originId: props?.originId }),
        )
      }
    },
  })
}

export const useImageBlobUpdateMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: ImagesImageIdBlobsBlobIdPatchRequest) =>
      ContentAPI.imageObjects.imagesImageIdBlobsBlobIdPatch(params),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: imageObjectQueryKeys.blobLists() })
    },
    onSettled: (data, error, variables) => {
      if (!error && !!data) {
        queryClient.resetQueries({ queryKey: imageObjectQueryKeys.blobLists() })

        queryClient.setQueriesData<InfiniteData<ImageBlobPaginatedList>>(
          { queryKey: imageObjectQueryKeys.blobLists() },
          updateListCachesByUpdate(data, { idKey: 'id', idToMatch: variables.blobId }),
        )

        return queryClient.invalidateQueries({ queryKey: imageObjectQueryKeys.blobDetails() })
      }
    },
  })
}

export const useImageBlobDeleteMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: ImagesImageIdBlobsBlobIdDeleteRequest) =>
      ContentAPI.imageObjects.imagesImageIdBlobsBlobIdDelete(params),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: imageObjectQueryKeys.blobLists() })
    },
    onSettled: (_, error, variables) => {
      if (!error) {
        queryClient.setQueriesData<InfiniteData<ImageBlobPaginatedList>>(
          { queryKey: imageObjectQueryKeys.blobLists() },
          updateListCachesByDeletion({ idKey: 'id', idToMatch: variables.blobId }),
        )
      }
    },
  })
}

export const useImageBlobReanalyzeMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: ImagesImageIdBlobsBlobIdReanalyzePostRequest) =>
      ContentAPI.imageObjects.imagesImageIdBlobsBlobIdReanalyzePost(params),
    onSettled: (_, error, variables) => {
      if (!error) {
        queryClient.invalidateQueries({
          queryKey: imageObjectQueryKeys.blobDetail({ imageId: variables.imageId, blobId: variables.blobId }).queryKey,
        })
      }
    },
  })
}
